import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// import ThemeToggle from './ThemeToggle';
import { getCityNameById } from '../utils/cityUtils';
import {
  Bars3Icon,
  DocumentTextIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Показывать боковую панель только для администратора
  const showSidebar = user?.type === 'admin';
  const isSalaryUser = user?.type === 'salary';

  const navigation = [
    // Для админа - только админ панель
    ...((user?.type === 'admin') ? [
      { name: 'Панель администратора', href: '/admin', icon: UserGroupIcon },
      { name: 'Отчеты', href: '/report', icon: DocumentTextIcon }
    ] : []),
    
    // Для менеджера - только панель менеджера
    ...(user?.type === 'manager' ? [
      { name: 'Панель менеджера', href: '/manager', icon: BuildingOfficeIcon },
    ] : []),
    
    // Для клиента - личный кабинет + создание сделок
    ...(user?.type === 'client' ? [
      { name: 'Личный кабинет', href: '/client', icon: DocumentTextIcon }
    ] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleDisplayName = (type: string) => {
    switch (type) {
      case 'admin':
        return 'Администратор';
      case 'manager':
        return 'Менеджер';
      case 'client':
        return 'Клиент';
      case 'salary':
        return 'Зарплата';
      default:
        return 'Пользователь';
    }
  };

  // Если не админ - показываем простой layout без боковой панели
  if (!showSidebar) {
    return (
      <div className="min-h-screen bg-base-200">
        {/* Верхняя панель */}
        <div className="navbar bg-base-100 border-b border-base-300">
          <div className="flex-1">
            <h1 className="text-xl font-bold text-base-content">
              AgentB24
            </h1>
          </div>
          {isSalaryUser && (
            <div className="mr-2">
              <button
                type="button"
                className={`btn btn-sm ${location.pathname === '/report' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => navigate('/report')}
              >
                <DocumentTextIcon className="h-4 w-4" />
                Отчеты
              </button>
            </div>
          )}
          
          {/* Переключатель темы - скрыт */}
          {/* <div className="flex-none mr-2">
            <ThemeToggle />
          </div> */}
          
          {/* Профиль пользователя - для не-админов */}
          <div className="flex-none">
            <div className="dropdown dropdown-end">
              <div tabIndex={0} role="button" className="btn btn-ghost btn-circle avatar">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-content flex items-center justify-center">
                  <UserCircleIcon className="h-6 w-6" />
                </div>
              </div>
              <ul tabIndex={0} className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 border border-base-300 rounded-box w-64">
                <li className="menu-title px-3 py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center">
                      <span className="text-xs font-bold">{user?.login?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-base-content">{user?.login}</div>
                      <div className="text-xs text-base-content/70">{getRoleDisplayName(user?.type || '')}</div>
                      {user?.category !== null && user?.category !== undefined && (
                        <div className="text-xs text-base-content/50">
                          Город: {getCityNameById(user.category)}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
                <div className="divider my-1"></div>
                <li>
                  <button onClick={handleLogout} className="text-error hover:bg-error/10">
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    Выйти
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Основной контент */}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  // Для администратора - показываем полный layout с боковой панелью
  return (
    <div className="drawer lg:drawer-open">
      <input id="main-drawer" type="checkbox" className="drawer-toggle" checked={sidebarOpen} onChange={(e) => setSidebarOpen(e.target.checked)} />
      
      <div className="drawer-content flex flex-col">
        {/* Верхняя панель */}
        <div className="navbar bg-base-100 border-b border-base-300">
          <div className="flex-none lg:hidden">
            <label htmlFor="main-drawer" className="btn btn-square btn-ghost">
              <Bars3Icon className="h-6 w-6" />
            </label>
          </div>
          
          <div className="flex-1">
            <h1 className="text-xl font-bold text-base-content">
              AgentB24
            </h1>
          </div>
          
          {/* Переключатель темы - скрыт */}
          {/* <div className="flex-none mr-2">
            <ThemeToggle />
          </div> */}
          
          {/* Профиль пользователя - для администратора */}
          <div className="flex-none">
            <div className="dropdown dropdown-end">
              <div tabIndex={0} role="button" className="btn btn-ghost btn-circle avatar">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-content flex items-center justify-center">
                  <UserCircleIcon className="h-6 w-6" />
                </div>
              </div>
              <ul tabIndex={0} className="menu menu-sm dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 border border-base-300 rounded-box w-64">
                <li className="menu-title px-3 py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center">
                      <span className="text-xs font-bold">{user?.login?.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-base-content">{user?.login}</div>
                      <div className="text-xs text-base-content/70">{getRoleDisplayName(user?.type || '')}</div>
                      {user?.category !== null && user?.category !== undefined && (
                        <div className="text-xs text-base-content/50">
                          Город: {getCityNameById(user.category)}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
                <div className="divider my-1"></div>
                <li>
                  <button onClick={handleLogout} className="text-error hover:bg-error/10">
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    Выйти
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Основной контент */}
        <main className="flex-1 p-6 bg-base-200">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
      
      {/* Боковая панель - только для администратора */}
      <div className="drawer-side">
        <label htmlFor="main-drawer" aria-label="close sidebar" className="drawer-overlay"></label>
        <aside className="min-h-full w-80 bg-base-100 border-r border-base-300">
          <div className="p-6">
            {/* Информация о пользователе */}
            <div className="bg-base-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-primary-content">{user?.login?.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-base-content">{user?.login}</p>
                  <p className="text-xs text-base-content/70">{getRoleDisplayName(user?.type || '')}</p>
                  {user?.category !== null && user?.category !== undefined && (
                    <div className="text-xs text-base-content/50">
                      Город: {getCityNameById(user.category)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Навигация */}
            <div>
              <h3 className="text-sm font-semibold text-base-content/70 uppercase tracking-wider mb-3">Навигация</h3>
              <ul className="menu menu-lg space-y-3">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className={`rounded-lg transition-all duration-200 ${
                          isActive 
                            ? 'bg-primary text-primary-content shadow-md' 
                            : 'hover:bg-base-200 text-base-content'
                        }`}
                      >
                        <item.icon className="h-5 w-5" />
                        {item.name}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Layout;
